// CardDesign5-themed ProductsServicesPreview component
import React, { useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    Button,
    Modal,
    IconButton,
    CircularProgress
} from '@mui/material';
import { CloseIcon, PlayArrowIcon, ChevronLeftIcon, ChevronRightIcon } from 'icons/materialIcons';
import { cardDesign5Theme } from 'team/shared/themes';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import styles from './TeamProductsServicesPreviewCardDesign5.module.css';

const TeamProductsServicesPreviewCardDesign5 = ({
    link,
    cardLinkColor = '#000',
    cardFont = 'Inter',
    isFullDisplay = false
}) => {
    const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [videoUrl, setVideoUrl] = useState(null);
    const [mediaLoadingErrors, setMediaLoadingErrors] = useState(new Set());

    if (!link || !link.details) return null;

    // Handle both Products & Services structure and regular contact link structure
    const {
        productTitle,
        description,
        price,
        category,
        features = [],
        images = [],
        videos = [],
        ctaButton,
        // Contact link structure fallbacks
        linkTitle,
        linkUrl,
        additionalDetails = {}
    } = link.details;

    // Use contact link structure if Products & Services structure is not available
    const displayTitle = productTitle || linkTitle || link.type || 'Products & Services';
    const displayDescription = description || additionalDetails.description || '';
    const displayImages = Array.isArray(additionalDetails.images) ? additionalDetails.images : [];
    const displayVideos = Array.isArray(additionalDetails.videos) ? additionalDetails.videos : [];
    const displayCtaButton = ctaButton || additionalDetails.ctaButton || {
        text: 'Learn More',
        link: additionalDetails.link || linkUrl || ''
    };

    // Get CardDesign5 theme
    const theme = cardDesign5Theme;

    // Enhanced media processing with better error handling
    const processMedia = () => {
        const processedImages = (Array.isArray(displayImages) ? displayImages : [])
            .filter(image => image && typeof image === 'string')
            .map((image, index) => ({
                type: 'image',
                src: image,
                thumbnail: image,
                originalIndex: index,
                id: `img-${index}-${image.substring(image.lastIndexOf('/') + 1, image.lastIndexOf('/') + 10)}`
            }));

        const processedVideos = (Array.isArray(displayVideos) ? displayVideos : [])
            .map((video, index) => {
                // Handle both string and object formats
                if (typeof video === 'string') {
                    return {
                        type: 'video',
                        src: video,
                        thumbnail: null,
                        originalIndex: index,
                        videoObject: video,
                        id: `vid-${index}-${video.substring(video.lastIndexOf('/') + 1, video.lastIndexOf('/') + 10)}`
                    };
                } else if (video && typeof video === 'object') {
                    return {
                        type: 'video',
                        src: video.src || video,
                        thumbnail: video.thumbnail || null,
                        originalIndex: index,
                        videoObject: video,
                        id: `vid-${index}-${(video.src || video).substring((video.src || video).lastIndexOf('/') + 1, (video.src || video).lastIndexOf('/') + 10)}`
                    };
                }
                return null;
            })
            .filter(Boolean);

        return [...processedImages, ...processedVideos];
    };

    const allMedia = processMedia();
    const totalMediaCount = allMedia.length;
    const currentMedia = allMedia[selectedMediaIndex];

    // Navigate through all media
    const nextMedia = () => {
        setSelectedMediaIndex((prev) => (prev + 1) % totalMediaCount);
    };

    const prevMedia = () => {
        setSelectedMediaIndex((prev) => (prev - 1 + totalMediaCount) % totalMediaCount);
    };

    // Handle CTA button click
    const handleCtaClick = () => {
        if (displayCtaButton?.link) {
            const url = displayCtaButton.link.startsWith('http')
                ? displayCtaButton.link
                : `https://${displayCtaButton.link}`;
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    // Enhanced video click handler with better platform support
    const handleVideoClick = (video) => {
        let videoSrc;

        if (typeof video === 'string') {
            videoSrc = video;
        } else if (video && typeof video === 'object') {
            videoSrc = video.src || video;
        } else {
            console.error('Invalid video format:', video);
            return;
        }

        if (videoSrc.startsWith('http')) {
            let embedUrl = videoSrc;

            // Enhanced YouTube support
            if (videoSrc.includes('youtube.com') || videoSrc.includes('youtu.be')) {
                const videoId = extractYouTubeVideoId(videoSrc);
                if (videoId) {
                    embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;
                }
            }
            // Enhanced Vimeo support
            else if (videoSrc.includes('vimeo.com')) {
                const vimeoId = videoSrc.split('vimeo.com/')[1]?.split('/')[0];
                if (vimeoId) {
                    embedUrl = `https://player.vimeo.com/video/${vimeoId}?autoplay=1`;
                }
            }

            setVideoUrl(embedUrl);
            setIsVideoPlaying(true);
        } else {
            setVideoUrl(videoSrc);
            setIsVideoPlaying(true);
        }
    };

    // Enhanced YouTube video ID extraction
    const extractYouTubeVideoId = (url) => {
        try {
            const parsed = new URL(url);
            if (parsed.hostname === 'youtu.be') {
                return parsed.pathname.slice(1);
            }
            if (parsed.hostname.includes('youtube.com')) {
                if (parsed.pathname.startsWith('/watch')) {
                    return parsed.searchParams.get('v');
                }
                if (parsed.pathname.startsWith('/embed/')) {
                    return parsed.pathname.split('/embed/')[1].split('?')[0];
                }
                if (parsed.pathname.startsWith('/shorts/')) {
                    return parsed.pathname.split('/shorts/')[1].split('/')[0];
                }
            }
        } catch {
            return null;
        }
        return null;
    };

    // Handle media loading errors
    const handleMediaError = (mediaId) => {
        setMediaLoadingErrors(prev => new Set([...prev, mediaId]));
    };

    // Enhanced main media click handler
    const handleMainMediaClick = () => {
        if (!currentMedia) return;

        if (currentMedia.type === 'image') {
            setIsGalleryOpen(true);
        } else if (currentMedia.type === 'video') {
            handleVideoClick(currentMedia.videoObject || currentMedia.src);
        }
    };

    return (
        <>
            {/* Product Card Container with CardDesign5 theme */}
            <Box
                sx={{
                    backgroundColor: '#f6f6f6',
                    borderRadius: '12px 12px 0 0',
                    overflow: 'hidden',
                    mb: 2,
                    p: 0
                }}
            >
                {/* Product Info Section */}
                <Box sx={{
                    p: { xs: '0.75rem', sm: '1rem', md: '1.25rem' }
                }}>
                    {/* Product Name */}
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily: `"${cardFont}", sans-serif`,
                            fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                            fontWeight: 600,
                            color: 'rgb(31, 41, 55)',
                            mb: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {displayTitle}
                    </Typography>

                    {/* Description */}
                    {displayDescription && (
                        <Typography
                            variant="body2"
                            sx={{
                                fontFamily: theme.card?.cardDesign5?.fontFamily || 'inherit',
                                color: 'rgb(107, 114, 128)',
                                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                                lineHeight: 1.5,
                                mb: 1.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical'
                            }}
                        >
                            {displayDescription}
                        </Typography>
                    )}
                </Box>

                {/* MAIN MEDIA SECTION with enhanced error handling */}
                {totalMediaCount > 0 && (
                    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto', mb: 2 }}>
                        <Swiper
                            modules={[Navigation, Pagination, A11y]}
                            navigation={true}
                            pagination={{ type: 'fraction', clickable: true }}
                            spaceBetween={16}
                            slidesPerView={1}
                            loop={false}
                            rewind={false}
                            style={{ borderRadius: 12 }}
                            a11y={{ enabled: true }}
                            onSlideChange={swiper => setSelectedMediaIndex(swiper.activeIndex)}
                            onSwiper={(swiper) => {
                                const handleArrowClick = (e) => {
                                    if (e.target.closest('.swiper-button-disabled')) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        e.stopImmediatePropagation();
                                        return false;
                                    }
                                };

                                setTimeout(() => {
                                    if (!swiper || !swiper.el) return;
                                    const nextButton = swiper.el.querySelector('.swiper-button-next');
                                    const prevButton = swiper.el.querySelector('.swiper-button-prev');

                                    if (nextButton) {
                                        nextButton.addEventListener('click', handleArrowClick, true);
                                    }
                                    if (prevButton) {
                                        prevButton.addEventListener('click', handleArrowClick, true);
                                    }
                                }, 100);
                            }}
                            initialSlide={selectedMediaIndex}
                        >
                            {allMedia.map((media, index) => (
                                <SwiperSlide key={media.id}>
                                    {media.type === 'image' ? (
                                        <Box
                                            component="img"
                                            src={media.src}
                                            alt={productTitle}
                                            sx={{
                                                width: '100%',
                                                height: { xs: 200, sm: 300 },
                                                objectFit: 'cover',
                                                borderRadius: 2,
                                                cursor: 'pointer',
                                                backgroundColor: '#f5f5f5',
                                                boxShadow: 1
                                            }}
                                            onClick={() => setIsGalleryOpen(true)}
                                            onError={() => handleMediaError(media.id)}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: { xs: 200, sm: 300 },
                                                position: 'relative',
                                                backgroundColor: '#f5f5f5',
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                boxShadow: 1
                                            }}
                                            onClick={() => handleVideoClick(media.videoObject || media.src)}
                                        >
                                            {media.thumbnail && !mediaLoadingErrors.has(media.id) ? (
                                                <img
                                                    src={media.thumbnail}
                                                    alt={`Video: ${productTitle}`}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    onError={() => handleMediaError(media.id)}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        height: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: '#f0f0f0',
                                                        flexDirection: 'column'
                                                    }}
                                                >
                                                    <PlayArrowIcon sx={{ fontSize: '3rem', color: '#666', mb: 1 }} />
                                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', fontFamily: `"${cardFont}", sans-serif` }}>
                                                        Video Content
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Enhanced play button overlay */}
                                            <IconButton
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    color: 'white',
                                                    width: 64,
                                                    height: 64,
                                                    backgroundColor: 'rgba(0,0,0,0.7)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0,0,0,0.8)',
                                                        transform: 'translate(-50%, -50%) scale(1.1)'
                                                    },
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <PlayArrowIcon fontSize="large" />
                                            </IconButton>
                                        </Box>
                                    )}
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </Box>
                )}

                {/* Remaining Product Info Section */}
                <Box sx={{
                    p: { xs: '0.75rem', sm: '1rem', md: '1.25rem' }
                }}>
                    {/* Enhanced media thumbnails gallery */}
                    {totalMediaCount > 1 && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                                {allMedia.map((media, index) => (
                                    <Box
                                        key={`thumbnail-${media.id}`}
                                        onClick={() => setSelectedMediaIndex(index)}
                                        sx={{
                                            minWidth: 60,
                                            height: 60,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            border: index === selectedMediaIndex ? `2px solid ${cardLinkColor}` : '2px solid transparent',
                                            opacity: index === selectedMediaIndex ? 1 : 0.8,
                                            transition: 'all 0.2s',
                                            position: 'relative',
                                            backgroundColor: '#f8f9fa'
                                        }}
                                    >
                                        {media.type === 'image' ? (
                                            <Box
                                                component="img"
                                                src={media.src}
                                                alt={`${productTitle} ${index + 1}`}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                                onError={() => handleMediaError(`thumb-${media.id}`)}
                                            />
                                        ) : (
                                            <>
                                                {media.thumbnail && !mediaLoadingErrors.has(`thumb-${media.id}`) ? (
                                                    <img
                                                        src={media.thumbnail}
                                                        alt={`Video thumbnail ${index + 1}`}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        onError={() => handleMediaError(`thumb-${media.id}`)}
                                                    />
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: '#f0f0f0'
                                                        }}
                                                    >
                                                        <PlayArrowIcon sx={{ color: '#666', fontSize: '1.5rem' }} />
                                                    </Box>
                                                )}
                                                <PlayArrowIcon
                                                    sx={{
                                                        color: 'white',
                                                        fontSize: '1.2rem',
                                                        position: 'absolute',
                                                        left: '50%',
                                                        top: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        pointerEvents: 'none',
                                                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                                                    }}
                                                />
                                            </>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Category and Price Row */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1.5
                    }}>
                        {category && (
                            <Chip
                                label={category}
                                size="small"
                                variant="outlined"
                                sx={{
                                    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                    fontFamily: theme.card?.cardDesign5?.fontFamily || 'inherit',
                                    borderColor: 'rgb(209, 213, 219)',
                                    color: 'rgb(75, 85, 99)'
                                }}
                            />
                        )}
                        {price && (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontFamily: `"${cardFont}", sans-serif`,
                                    fontWeight: 600,
                                    color: cardLinkColor,
                                    fontSize: { xs: '1rem', sm: '1.125rem' }
                                }}
                            >
                                {price}
                            </Typography>
                        )}
                    </Box>

                    {/* Features */}
                    {features.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {features.slice(0, 3).map((feature, index) => (
                                    <Chip
                                        key={index}
                                        label={feature}
                                        size="small"
                                        sx={{
                                            fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                                            height: 24,
                                            fontFamily: theme.card?.cardDesign5?.fontFamily || 'inherit',
                                            backgroundColor: 'rgba(0,0,0,0.05)',
                                            '& .MuiChip-label': { px: 1 }
                                        }}
                                    />
                                ))}
                                {features.length > 3 && (
                                    <Chip
                                        label={`+${features.length - 3} more`}
                                        size="small"
                                        sx={{
                                            fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                                            height: 24,
                                            fontFamily: theme.card?.cardDesign5?.fontFamily || 'inherit',
                                            backgroundColor: 'rgba(0,0,0,0.1)',
                                            '& .MuiChip-label': { px: 1 }
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* CTA Button */}
                    {displayCtaButton?.text && (
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleCtaClick}
                            sx={{
                                backgroundColor: cardLinkColor,
                                fontFamily: theme.card?.cardDesign5?.fontFamily || 'inherit',
                                textTransform: 'none',
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                fontWeight: 600,
                                py: { xs: 1, sm: 1.25 },
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: cardLinkColor,
                                    opacity: 0.9
                                }
                            }}
                        >
                            {displayCtaButton.text}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Enhanced Image Gallery Modal */}
            <Modal
                open={isGalleryOpen}
                onClose={() => setIsGalleryOpen(false)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(4px)'
                }}
            >
                <Box sx={{
                    position: 'relative',
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    outline: 'none'
                }}>
                    <IconButton
                        onClick={() => setIsGalleryOpen(false)}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            zIndex: 1,
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {currentMedia && currentMedia.type === 'image' && (
                        <img
                            src={currentMedia.src}
                            alt={productTitle}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                borderRadius: 8
                            }}
                        />
                    )}
                    {totalMediaCount > 1 && (
                        <>
                            <IconButton
                                onClick={prevMedia}
                                sx={{
                                    position: 'absolute',
                                    left: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                                }}
                            >
                                <ChevronLeftIcon />
                            </IconButton>
                            <IconButton
                                onClick={nextMedia}
                                sx={{
                                    position: 'absolute',
                                    right: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                                }}
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </>
                    )}
                </Box>
            </Modal>

            {/* Enhanced Video Modal */}
            <Modal
                open={isVideoPlaying}
                onClose={() => setIsVideoPlaying(false)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(4px)'
                }}
            >
                <Box sx={{
                    position: 'relative',
                    width: '90%',
                    maxWidth: '1200px',
                    aspectRatio: '16/9',
                    outline: 'none',
                    backgroundColor: 'black',
                    borderRadius: '8px',
                    overflow: 'hidden'
                }}>
                    <IconButton
                        onClick={() => setIsVideoPlaying(false)}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            zIndex: 1,
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {videoUrl && (
                        videoUrl.startsWith('http') && (videoUrl.includes('youtube') || videoUrl.includes('vimeo')) ? (
                            <iframe
                                src={videoUrl}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none'
                                }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                                title="Video player"
                            />
                        ) : (
                            <video
                                src={videoUrl}
                                controls
                                autoPlay
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain'
                                }}
                                playsInline
                            />
                        )
                    )}
                </Box>
            </Modal>
        </>
    );
};

export default TeamProductsServicesPreviewCardDesign5;